import type { CandidateInterviewActionV1 } from '@/shared/api';
export function resultHref(
	interviewId: string,
	attempt?: { id: string; status: string; action?: CandidateInterviewActionV1 },
	completionHref?: string,
) {
	return attempt?.status === 'completed'
		? (completionHref ??
				(attempt.action && attempt.action !== 'ui_report'
					? `/interviews/${encodeURIComponent(interviewId)}/complete?attempt=${encodeURIComponent(attempt.id)}`
					: `/interviews/${encodeURIComponent(interviewId)}?attempt=${encodeURIComponent(attempt.id)}`))
		: null;
}

export function reportLabel(report?: {
	outcome?: string;
	outcomeLabel?: string;
	grade?: { code: string; title: string } | null;
}) {
	if (!report) return '';
	if (report.outcomeLabel) return report.outcomeLabel;
	if (report.outcome === 'no_hire') return report.outcomeLabel || 'No Hire';
	const grade = report.grade;
	if (grade)
		return grade.code === grade.title
			? grade.code
			: `${grade.code} · ${grade.title}`;
	return report.outcomeLabel || '';
}

export function formatScore(value: number) {
	return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(
		value,
	);
}

export function formatTaskDuration(seconds?: number | null) {
	return seconds == null ? '—' : `${Math.ceil(seconds / 60)} мин`;
}

export function mergeAttemptHistory<T extends { id: string }>(
	current: T[],
	incoming: T[],
): T[] {
	const attempts = new Map(
		current.map((attempt) => [attempt.id.toLowerCase(), attempt]),
	);
	for (const attempt of incoming)
		attempts.set(attempt.id.toLowerCase(), attempt);
	return [...attempts.values()];
}

export type CandidateEvent =
	| 'promo_outcome_view'
	| 'promo_sales_redirect'
	| 'promo_sales_redirect_error'
	| 'result_open'
	| 'redirect'
	| 'delivery_view'
	| 'delivery_check'
	| 'delivery_result'
	| 'telegram_link'
	| 'plans_view'
	| 'plans_result'
	| 'plans_retry'
	| 'plan_select'
	| 'selection_pending'
	| 'selection_success'
	| 'selection_error'
	| 'selection_status_retry'
	| 'description_expand'
	| 'description_collapse'
	| 'page_view'
	| 'start_intent'
	| 'start_success'
	| 'continue_intent'
	| 'continue_success'
	| 'session_view'
	| 'finish_intent'
	| 'finish_cancel'
	| 'finish_success'
	| 'skip_intent'
	| 'next_intent'
	| 'advance_success'
	| 'report_view'
	| 'report_close'
	| 'criterion_view'
	| 'criterion_close'
	| 'tasks_view'
	| 'tasks_close'
	| 'task_open'
	| 'replay_view'
	| 'replay_file_open'
	| 'replay_file_close'
	| 'back_to_result'
	| 'career_offer_click'
	| 'catalog_exit'
	| 'history_more'
	| 'request_error'
	| 'retry'
	| 'recovery';
export type CandidateEventContext = {
	courseId?: string;
	interviewId?: string;
	interviewType?: string;
	entryPoint?: string;
	reportStatus?: string;
	action?: string;
	position?: number;
	criterionKey?: string;
	planId?: string;
	outcome?: string;
	hasHistory?: boolean;
	hasActiveAttempt?: boolean;
	[key: string]: unknown;
};

export function trackCandidateEvent(
	reachGoal: (name: string, parameters?: Record<string, unknown>) => unknown,
	event: CandidateEvent,
	context: CandidateEventContext,
) {
	const allowed = {
		interviewId: 'interview_id',
		interviewType: 'interview_type',
		entryPoint: 'entry_point',
		reportStatus: 'report_status',
		action: 'action',
		position: 'position',
		criterionKey: 'criterion_key',
		planId: 'plan_id',
		courseId: 'course_id',
		outcome: 'outcome',
		hasHistory: 'has_history',
		hasActiveAttempt: 'has_active_attempt',
	} as const;
	const parameters: Record<string, string | number | boolean> = {};
	for (const [key, name] of Object.entries(allowed)) {
		const value = context[key];
		if (
			typeof value === 'string' ||
			typeof value === 'boolean' ||
			(typeof value === 'number' && Number.isFinite(value))
		)
			parameters[name] = value;
	}
	try {
		reachGoal(`CANDIDATE_INTERVIEW_${event.toUpperCase()}`, {
			candidate_interview: parameters,
		});
	} catch {
		/* Analytics never interrupts the interview. */
	}
}

export function createViewTracker() {
	const viewed = new Set<string>();
	return (key: string) => {
		if (viewed.has(key)) return false;
		viewed.add(key);
		return true;
	};
}
